import { useEffect, useState, useCallback } from "react";
import {
  Typography, Card, Box, Paper, IconButton,
  Toolbar, Stack, Avatar, InputBase, CardMedia, Button, Drawer,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SearchIcon from "@mui/icons-material/Search";
// import API, { logEvent } from "../api/api";
import API from "../api/api";
import AddressDrawer from "../components/AddressDrawer";
import GlobalSkeleton from "../components/GlobalSkeleton";
import { useLocation } from "react-router-dom";
import {
  getCachedData,
  setCachedData
} from "../utils/cache";

export default function MenuPage() {
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentAddress, setCurrentAddress] = useState(null);
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [openAddressDrawer, setOpenAddressDrawer] = useState(false);
  const [tempQuantity, setTempQuantity] = useState(1);
  const location = useLocation();
  const highlightedItemId = location.state?.highlightedItemId;

  // ---> NEW STATE FOR ACTIVE CATEGORY <---
  const [activeCategory, setActiveCategory] = useState(location.state?.selectedCategory || null);

  const searchAliases = {
    rice: ["steamed rice", "biriyani", "raita"],
    chicken: ["chicken curry", "chicken chap", "chicken roll", "steamed momo", "chowmin", "biriyani"],
    fish: ["fish curry"],
    veg: ["rice", "sabji", "raita", "dhokla"],
    nonveg: ["fish", "fish curry", "chicken curry", "chicken chap", "chicken roll", "steamed momo", "chowmin", "biriyani"],
    bengali: ["fish curry", "biriyani"],
    spicy: ["chicken chap", "chicken curry", "biriyani"],
    snacks: ["dhokla", "chicken roll", "steamed momo", "chowmin"],
    healthy: ["steamed rice", "fish curry", "raita", "dhokla"]
  };

  const syncCartFromStorage = useCallback(() => {
    const savedCart = localStorage.getItem("mealnoCart");
    if (savedCart) setCart(JSON.parse(savedCart));
    else setCart([]);
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("selectedAddress");
    if (saved) {
      const addr = JSON.parse(saved);
      setCurrentAddress(addr);
      fetchMenu(addr.lat, addr.lng);
    }
    syncCartFromStorage();

    const handleUpdate = (e) => {
      syncCartFromStorage();
      const savedAddr = localStorage.getItem("selectedAddress");
      if (savedAddr) {
        const newAddr = JSON.parse(savedAddr);
        setCurrentAddress(prevAddr => {
          if (prevAddr?.lat !== newAddr.lat || prevAddr?.lng !== newAddr.lng) {
            fetchMenu(newAddr.lat, newAddr.lng);
            return newAddr;
          }
          return prevAddr;
        });
      }
    };

    window.addEventListener("storage", handleUpdate);
    return () => window.removeEventListener("storage", handleUpdate);
  }, [syncCartFromStorage]);

  const fetchMenu = async (lat, lng) => {
    if (!lat || !lng) return;

    const cacheKey = `menu_${lat}_${lng}`;
    const cachedMenu = getCachedData(cacheKey);

    if (cachedMenu) {
      setMenu(cachedMenu);
    } else {
      setLoading(true);
    }

    try {
      setLoading(true);
      const res = await API.post("/menu-by-location", { lat, lng });
      if (res.data.menu) {
        setMenu(res.data.menu);
        setCachedData(cacheKey, res.data.menu);
      }
    } catch (err) {
      console.error("No service here");
    } finally {
      setLoading(false);
    }
  };

  const updateCartQuantity = (item, change) => {
    let currentCart = [...cart];
    const existingIndex = currentCart.findIndex((i) => i.id === item.id);
    if (existingIndex !== -1) {
      currentCart[existingIndex].quantity += change;
      if (currentCart[existingIndex].quantity <= 0) currentCart.splice(existingIndex, 1);
    } else if (change > 0) {
      currentCart.push({
        ...item,
        display_price: item.display_price || item.selling_price,
        quantity: change
      });
    }

    // event log
    // if (change > 0) logEvent("ADD_TO_CART", { menu_item_id: item.id, vendor_id: item.vendor_id });

    localStorage.setItem("mealnoCart", JSON.stringify(currentCart));
    setCart(currentCart);
    window.dispatchEvent(new Event("storage"));
  };

  const getItemQuantity = (id) => cart.find((i) => i.id === id)?.quantity || 0;

  // ---> UPDATED FILTER LOGIC TO INCLUDE CATEGORY <---
  const filteredMenu = menu.filter((item) => {
    const query = searchQuery.toLowerCase();
    const aliases = searchAliases[query] || [];

    const matchesSearch = query === "" || (
      item.name?.toLowerCase().includes(query) ||
      item.category?.toLowerCase().includes(query) ||
      item.description?.toLowerCase().includes(query) ||
      aliases.some(alias =>
        item.name?.toLowerCase().includes(alias) ||
        item.description?.toLowerCase().includes(alias)
      )
    );

    // FIX: Match main category (Veg/Non Veg) OR check if it exists in the food_tags array
    const matchesCategory = !activeCategory ||
      item.category === activeCategory ||
      (item.food_tags && item.food_tags.some(tag => tag.name === activeCategory));

    return matchesSearch && matchesCategory;
  });


  // Fallback to the full menu if the search/filter yields zero results (matching Home Page behavior)
  const displayMenu = filteredMenu.length > 0 ? [...filteredMenu] : [...menu];

  displayMenu.sort((a, b) => {
    // 1. ABSOLUTE PRIORITY: The item just clicked from the LandingHome page
    if (highlightedItemId) {
      if (a.id === highlightedItemId && b.id !== highlightedItemId) return -1;
      if (b.id === highlightedItemId && a.id !== highlightedItemId) return 1;
    }

    // 2. CART PRIORITY: Items in the cart float to the top
    const cartIndexA = cart.findIndex(c => c.id === a.id);
    const cartIndexB = cart.findIndex(c => c.id === b.id);

    const inCartA = cartIndexA !== -1;
    const inCartB = cartIndexB !== -1;

    if (inCartA && inCartB) {
      return cartIndexB - cartIndexA;
    }

    if (inCartA) return -1;
    if (inCartB) return 1;

    // 3. AVAILABILITY PRIORITY: Push out-of-stock items to the bottom
    if (a.can_order !== b.can_order) return a.can_order ? -1 : 1;

    // 4. ADMIN PRIORITY: Show recommended items at the top of the available list
    if (a.is_priority !== b.is_priority) return a.is_priority ? -1 : 1;

    // 5. MANUAL RANK TIE-BREAKER: Sort by the admin's custom number
    const rankA = a.sort_order || 0;
    const rankB = b.sort_order || 0;

    if (rankA !== rankB) {
      return rankA - rankB;
    }

    // Fallback: Alphabetical A-Z
    if (a.name && b.name) {
      return a.name.localeCompare(b.name);
    }

    return 0;
  });

  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    const isAnyModalOpen = openAddressDrawer || !!selectedItem;
    if (isAnyModalOpen) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    window.dispatchEvent(new Event("modal-state-change"));

    return () => {
      document.body.classList.remove("modal-open");
      window.dispatchEvent(new Event("modal-state-change"));
    };
  }, [openAddressDrawer, selectedItem]);

  return (
    // Theme: Soft App Background
    <Box sx={{ minHeight: "100vh", bgcolor: "#FFFDF8", mb: 5 }}>

      {/* HEADER (Original Structure + Theme Colors) */}
      <Box sx={{ pt: 0, zIndex: 1100, background: "#F8D548", borderRadius: "0 0 40px 40px", borderBottom: '2px solid #c3a169c4' }}>
        <Toolbar sx={{ py: 1, justifyContent: 'space-between' }}>
          <Box onClick={() => setOpenAddressDrawer(true)} sx={{ display: "flex", alignItems: "center", color: "#3A2C23", cursor: "pointer" }}>
            <Avatar sx={{ bgcolor: "#F8C332", mr: 1.5, width: 35, height: 35 }}>
              <LocationOnIcon sx={{ color: "#3A2C23", fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight="900" sx={{ display: 'flex', alignItems: 'center' }}>
                {currentAddress?.address_type || "Select Location"}
                <KeyboardArrowDownIcon fontSize="small" sx={{ ml: 0.5 }} />
              </Typography>
              <Typography variant="caption" sx={{ color: "#6E655E", opacity: 0.8, display: 'block', maxWidth: '180px' }} noWrap>
                {currentAddress ? `${currentAddress.flat}, ${currentAddress.building}` : "Set your location"}
              </Typography>
            </Box>
          </Box>
        </Toolbar>
        <Box sx={{ px: 2, pb: activeCategory ? 1 : 2 }}>
          <Paper
            elevation={0}
            sx={{
              p: "2px 15px",
              display: "flex",
              alignItems: "center",
              borderRadius: "50px",
              bgcolor: "#fff",
              height: 44,
              border: 'none',
              boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)',
              }
            }}
          >
            <SearchIcon sx={{ color: "#7A7068", mr: 1 }} />
            <InputBase
              sx={{ flex: 1, '&::placeholder': { color: '#A59D96' }, color: '#2F2A25' }}
              placeholder="Search food..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {/* Added Clear Button Logic Here */}
            {searchQuery && (
              <IconButton
                size="small"
                onClick={() => setSearchQuery("")}
                sx={{ color: '#A59D96', p: 0.5, ml: 0.5 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Paper>
        </Box>

        {/* ---> NEW ACTIVE CATEGORY INDICATOR <--- */}
        {activeCategory && (
          <Box sx={{ px: 3, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" fontWeight="800" color="#3A2C23">
              Category: <span style={{ color: '#E85D3F' }}>{activeCategory}</span>
            </Typography>
            <Button
              size="small"
              onClick={() => setActiveCategory(null)}
              disableElevation
              sx={{
                textTransform: 'none',
                color: '#6E655E',
                fontWeight: '800',
                bgcolor: '#FFF3C4',
                borderRadius: '8px',
                px: 1.5,
                py: 0.5,
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                '&:hover': { bgcolor: '#F5E6A3' }
              }}
            >
              Clear
            </Button>
          </Box>
        )}
      </Box>

      {/* --- CONDITIONAL MENU RENDERING --- */}
      {!currentAddress ? (
        <Box
          sx={{
            px: 2,
            mt: 6,
            mb: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center'
          }}
        >
          <Avatar
            sx={{
              bgcolor: '#FFFDF8',
              width: 80,
              height: 80,
              mb: 2,
              border: '2px dashed #E0D8C8'
            }}
          >
            <LocationOnIcon sx={{ color: '#F8C332', fontSize: 40 }} />
          </Avatar>
          <Typography variant="h6" fontWeight="900" sx={{ color: "#3A2C23", mb: 1 }}>
            Where are we delivering?
          </Typography>
          <Typography variant="body2" sx={{ color: "#8E867E", mb: 4, maxWidth: '280px' }}>
            Please select your address to see the menu and offers available in your area.
          </Typography>
          <Button
            variant="contained"
            disableElevation
            onClick={() => setOpenAddressDrawer(true)}
            sx={{
              borderRadius: "16px", py: 1.5, px: 4, fontWeight: "800",
              bgcolor: "#F8C332", color: "#3A2C23",
              '&:hover': { bgcolor: '#F5B71D' },
              textTransform: "none", fontSize: "1rem",
              boxShadow: '0 4px 12px rgba(248, 195, 50, 0.3)'
            }}
          >
            Select Address
          </Button>
        </Box>
      ) : (
        <Box sx={{ p: 2, pb: totalItems > 0 ? 25 : 10 }}>
          {searchQuery.trim() !== "" && filteredMenu.length === 0 && (
            <Box
              sx={{
                mb: 2, p: 1.5, bgcolor: "#FFFFFF", borderRadius: "12px",
                border: "1px solid #ECE5DA"
              }}
            >
              <Typography sx={{ color: "#E85D3F", fontWeight: 700, textAlign: "center" }}>
                No exact match found for "{searchQuery}". Showing other popular food items.
              </Typography>
            </Box>
          )}

          {loading && menu.length === 0 ? (
            <GlobalSkeleton type="menu" />
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                columnGap: 1.5,
                rowGap: 4 // Restored Original rowGap
              }}
            >
              {displayMenu.map((item) => {
                const quantity = getItemQuantity(item.id);

                return (
                  <Card
                    key={item.id}
                    elevation={0}
                    sx={{
                      borderRadius: "20px",
                      p: 1,
                      textAlign: 'left',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      minWidth: 0,
                      overflow: 'hidden',
                      bgcolor: "#FFFFFF",
                      border: 'none', // Removed flat border
                      boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)', // Added 3D Shadow
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease', // Smooth transition
                      '&:hover': {
                        transform: 'translateY(-3px)', // Lift effect on hover
                        boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)',
                      }
                    }}
                    onClick={() => {
                      setSelectedItem(item);
                      setTempQuantity(1);
                    }}
                  >
                    {/* Item Image */}
                    {item.image ? (
                      <Box sx={{ position: "relative" }}>
                        <CardMedia
                          component="img"
                          height="100" // Restored Original height
                          image={item.image}
                          alt={item.name}
                          sx={{
                            borderRadius: "15px",
                            objectFit: "cover",
                            width: "100%",
                            filter: item.can_order ? "none" : "grayscale(100%)",
                            opacity: item.can_order ? 1 : 0.5
                          }}
                        />
                        {!item.can_order && (
                          <Box
                            sx={{
                              position: "absolute", top: 6, right: 6,
                              bgcolor: "#111", color: "#fff",
                              px: 1, py: 0.3, borderRadius: "8px",
                              fontSize: 10, fontWeight: 700
                            }}
                          >
                            Not Available
                          </Box>
                        )}

                      </Box>
                    ) : (
                      <Box sx={{ height: 100, borderRadius: "15px", bgcolor: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Typography fontSize={12} color="text.secondary">No Image</Typography>
                      </Box>
                    )}

                    {/* Change alignItems to 'flex-start' here */}
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', gap: 0.5, flexGrow: 1 }}>
                      <Box
                        sx={{
                          minWidth: 12, height: 12,
                          mt: 0.3, // Add a tiny margin-top so it aligns with the smaller text
                          border: `1.5px solid ${item.category?.toLowerCase().startsWith('n') ? '#E85D3F' : '#6A9E3A'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '2px'
                        }}
                      >
                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: item.category?.toLowerCase().startsWith('n') ? '#E85D3F' : '#6A9E3A' }} />
                      </Box>
                      <Typography
                        variant="body2"
                        fontWeight="800"
                        sx={{
                          color: item.can_order ? "#2F2A25" : "#A59D96",
                          fontSize: '0.8rem',
                          lineHeight: 1.3,
                          whiteSpace: 'normal',
                        }}
                      >
                        {item.name}
                      </Typography>
                    </Box>

                    {/* Price Box */}
                    <Box sx={{ mt: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        {item.display_price > item.selling_price && (
                          <Typography sx={{ textDecoration: "line-through", color: "#A59D96", fontSize: 12, fontWeight: 600 }}>
                            ₹{item.display_price}
                          </Typography>
                        )}
                        <Typography sx={{ color: "#2F2A25", fontWeight: 900, fontSize: 15 }}>
                          ₹{item.selling_price}
                        </Typography>
                      </Box>
                      {item.discount_percent > 0 && (
                        <Typography sx={{ color: "#E85D3F", fontWeight: 800, fontSize: 11, mt: 0.2 }}>
                          {item.discount_percent}% OFF
                        </Typography>
                      )}
                    </Box>

                    {/* Quantity Controls (Original Structure + Theme) */}
                    <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'center', width: '100%' }} onClick={(e) => e.stopPropagation()}>
                      {!item.can_order ? (
                        <Button
                          disabled fullWidth
                          sx={{ borderRadius: "8px", bgcolor: '#F2EEE8', color: '#A59D96', textTransform: 'none', fontWeight: 700 }}
                        >
                          Unavailable
                        </Button>
                      ) : quantity === 0 ? (
                        <Button
                          size="small"
                          variant="contained"
                          disableElevation
                          disabled={!item.can_order}
                          onClick={() => updateCartQuantity(item, 1)}
                          sx={{
                            borderRadius: '8px', textTransform: 'none', fontWeight: 900,
                            color: '#6A9E3A', bgcolor: '#DDECC8', width: '100%',
                            '&:hover': { bgcolor: '#cce3af' }
                          }}
                        >
                          Add
                        </Button>
                      ) : (
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%', bgcolor: '#FFFDF8', borderRadius: '8px', border: '1px solid #ECE5DA' }}>
                          <IconButton size="small" onClick={() => updateCartQuantity(item, -1)} sx={{ color: "#F29D17", p: 0.5 }}><RemoveIcon fontSize="small" /></IconButton>
                          <Typography fontWeight="900" fontSize="0.9rem" color="#2F2A25">{quantity}</Typography>
                          <IconButton size="small" onClick={() => updateCartQuantity(item, 1)} sx={{ color: "#F29D17", p: 0.5 }}><AddIcon fontSize="small" /></IconButton>
                        </Stack>
                      )}
                    </Box>
                  </Card>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* ITEM DETAILS POPUP (BOTTOM SHEET) */}
      <Drawer
        anchor="bottom"
        open={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        PaperProps={{
          sx: {
            borderRadius: "24px 24px 0 0", overflow: "visible",
            height: "75vh", maxHeight: "75vh", display: "flex",
            flexDirection: "column", bgcolor: "#FFFDF8"
          },
        }}
      >
        {selectedItem && (
          <>
            <IconButton
              onClick={() => setSelectedItem(null)}
              sx={{
                position: "absolute", top: -55, left: "50%", transform: "translateX(-50%)",
                bgcolor: "rgba(0,0,0,0.6)", color: "#fff", zIndex: 1300,
                border: "1px solid rgba(255,255,255,0.3)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* SCROLLABLE CONTENT */}
            <Box sx={{ overflowY: "auto", flexGrow: 1 }}>
              <Box sx={{ position: "relative", p: 0 }}>
                <CardMedia
                  component="img" height="240" image={selectedItem.image}
                  sx={{
                    borderRadius: "24px 24px 0 0",
                    objectFit: "cover",
                    boxShadow: '0 8px 24px rgba(58, 44, 35, 0.08)',
                    filter: selectedItem.can_order ? "none" : "grayscale(100%)",
                    opacity: selectedItem.can_order ? 1 : 0.6
                  }}
                />

              </Box>

              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>

                  {/* Left Side: Icon, Name, and Description */}
                  <Box sx={{ flex: 1, pr: 2 }}>

                    {/* 1. Veg/Non-Veg Icon on Top */}
                    <Box
                      sx={{
                        width: 18, height: 18, flexShrink: 0, mb: 1,
                        border: `2px solid ${selectedItem.category?.toLowerCase().startsWith("n") ? "#E85D3F" : "#6A9E3A"}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        borderRadius: "4px",
                        bgcolor: "#FFFFFF"
                      }}
                    >
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: selectedItem.category?.toLowerCase().startsWith("n") ? "#E85D3F" : "#6A9E3A" }} />
                    </Box>

                    {/* 2. Menu Item Name directly underneath */}
                    <Typography variant="h5" fontWeight="800" sx={{ color: "#3A2C23", lineHeight: 1.3, fontSize: "1.45rem", wordBreak: "break-word", mb: 1 }}>
                      {selectedItem.name}
                    </Typography>

                    {/* 3. Description below name */}
                    <Typography variant="body1" sx={{ color: "#6E655E", lineHeight: 1.4 }}>
                      {selectedItem.description || "Prepared fresh every day by trusted home chefs. Remixed for you."}
                    </Typography>
                  </Box>

                  {/* Right Side: Prices aligned to the right (No save/share icons for now) */}
                  <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                    {selectedItem.display_price > selectedItem.selling_price && (
                      <Typography sx={{ textDecoration: "line-through", color: "#A59D96", fontSize: 14, fontWeight: 600 }}>
                        ₹{selectedItem.display_price}
                      </Typography>
                    )}
                    <Typography sx={{ color: "#2F2A25", fontWeight: 800, fontSize: 22, lineHeight: 1.1 }}>
                      ₹{selectedItem.selling_price}
                    </Typography>
                    {selectedItem.discount_percent > 0 && (
                      <Typography sx={{ color: "#E85D3F", fontWeight: 800, fontSize: 12, bgcolor: '#FFF6DE', px: 1, py: 0.2, borderRadius: '8px', mt: 0.5, display: 'inline-block' }}>
                        {selectedItem.discount_percent}% OFF
                      </Typography>
                    )}
                  </Box>

                </Box>
              </Box>
            </Box>

            {/* STICKY BOTTOM ACTION BAR */}
            <Box
              sx={{
                p: 2, borderTop: "1px solid #ECE5DA", display: "flex", alignItems: "center", gap: 2, bgcolor: "#FFFFFF"
              }}
            >
              <Stack
                direction="row" alignItems="center" justifyContent="space-between"
                sx={{
                  border: "1px solid #ECE5DA", borderRadius: "12px", px: 0.5, py: 0.7, minWidth: "110px", bgcolor: "#FFFDF8",
                }}
              >
                <IconButton size="small" onClick={() => setTempQuantity((prev) => Math.max(1, prev - 1))} sx={{ color: "#F29D17" }}>
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <Typography fontWeight="900" sx={{ color: "#2F2A25" }}>
                  {tempQuantity}
                </Typography>
                <IconButton size="small" onClick={() => setTempQuantity((prev) => prev + 1)} sx={{ color: "#F29D17" }}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Stack>

              <Button
                fullWidth
                disabled={!selectedItem.can_order}
                variant="contained"
                disableElevation
                onClick={() => {
                  updateCartQuantity(selectedItem, tempQuantity);
                  setSelectedItem(null);
                }}
                sx={{
                  bgcolor: "#F8C332", color: "#3A2C23", borderRadius: "12px", py: 1,
                  fontWeight: "900", textTransform: "none", fontSize: "1rem",
                  '&:hover': { bgcolor: '#F5B71D' }
                }}
              >
                {selectedItem.can_order
                  ? `Add item - ₹${selectedItem.selling_price * tempQuantity}`
                  : "Currently Unavailable"}
              </Button>
            </Box>
          </>
        )}
      </Drawer>

      <AddressDrawer
        open={openAddressDrawer}
        onClose={() => setOpenAddressDrawer(false)}
        onSelect={(addr) => {
          setCurrentAddress(addr);
          localStorage.setItem("selectedAddress", JSON.stringify(addr));

          // ✅ Clear the cart because location has changed
          localStorage.removeItem("mealnoCart");
          setCart([]);
          // 👇 NEW: CLEAR COUPON WHEN ADDRESS CHANGES
          localStorage.removeItem("selectedCoupon");
          localStorage.removeItem("couponDiscount");
          fetchMenu(addr.lat, addr.lng);
          window.dispatchEvent(new Event("storage"));
          setOpenAddressDrawer(false);
        }}
      />
    </Box>
  );
}